// =====================================================
// 책 신청 관리 탭
// AdminLibraryManagement.tsx 안의 RequestTab 함수를 이 내용으로 교체
// =====================================================
import { useState, useEffect } from 'react'
import { getApiUrl, fetchWithAuth } from '../../../api'

interface BookRequestItem {
    id: number
    member_id: number
    member_name?: string | null
    book_title: string
    reason: string | null
    status: number // 0=구비신청, 1=구비완료
    created_at: string
}

function RequestTab() {
    const [requests, setRequests] = useState<BookRequestItem[]>([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [total, setTotal] = useState(0)
    const limit = 20

    const [confirmModal, setConfirmModal] = useState<{ open: boolean; item: BookRequestItem | null }>({ open: false, item: null })
    const [resultModal, setResultModal] = useState({ open: false, title: '' })

    const fetchRequests = async () => {
        setLoading(true)
        try {
            const params = new URLSearchParams({ page: String(page), limit: String(limit) })
            const res = await fetchWithAuth(getApiUrl(`/marubit/api/admin/book/request/list?${params.toString()}`))
            if (!res.ok) throw new Error('조회 실패')
            const json = await res.json()
            setRequests(json.data.requests)
            setTotal(json.data.total)
        } catch {
            setResultModal({ open: true, title: '목록을 불러오는 데 실패했습니다.' })
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => { fetchRequests() }, [page])

    const handleCompleteClick = (item: BookRequestItem) => setConfirmModal({ open: true, item })

    const handleCompleteConfirm = async () => {
        if (!confirmModal.item) return
        try {
            const token = localStorage.getItem('access_token')
            const res = await fetch(getApiUrl(`/marubit/api/admin/book/request/${confirmModal.item.id}/complete`), {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` },
            })
            if (!res.ok) {
                const err = await res.json()
                throw new Error(err?.error?.message || '처리 실패')
            }
            setResultModal({ open: true, title: '구비완료 처리되었습니다.' })
            fetchRequests()
        } catch (e: any) {
            setResultModal({ open: true, title: e.message ?? '처리에 실패했습니다.' })
        } finally {
            setConfirmModal({ open: false, item: null })
        }
    }

    const formatDate = (dateStr: string) =>
        new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' })

    const totalPages = Math.max(1, Math.ceil(total / limit))

    return (
        <>
            <div className="bg-white rounded-2xl border border-[#e8e0dc] overflow-hidden shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
                <table className="w-full">
                    <thead>
                        <tr className="bg-[#ede0d8] border-b border-[#e8e0dc]">
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium">도서명</th>
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[160px]">신청 사유</th>
                            <th className="text-left px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[120px]">신청일</th>
                            <th className="text-center px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[100px]">상태</th>
                            <th className="text-center px-4 py-3 text-[12px] text-[#5a3d32] font-medium w-[110px]">처리</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="text-center py-12 text-gray-400 text-[14px]">불러오는 중...</td></tr>
                        ) : requests.length === 0 ? (
                            <tr><td colSpan={5} className="text-center py-12 text-gray-300 text-[13px]">신청된 책이 없습니다.</td></tr>
                        ) : requests.map(item => (
                            <tr key={item.id} className="border-t border-[#f5f2f0] hover:bg-[#fdf8f0] transition-colors">
                                <td className="px-4 py-3 text-[13px] font-medium text-[#2a1f1a]">{item.book_title}</td>
                                <td className="px-4 py-3 text-[12px] text-gray-400 truncate max-w-[160px]">{item.reason ?? '-'}</td>
                                <td className="px-4 py-3 text-[12px] text-gray-400 whitespace-nowrap">{formatDate(item.created_at)}</td>
                                <td className="px-4 py-3 text-center">
                                    <span className={`text-[11px] px-2 py-0.5 rounded font-medium ${item.status === 1 ? 'bg-green-50 text-green-600' : 'bg-orange-50 text-orange-500'}`}>
                                        {item.status === 1 ? '구비완료' : '구비신청'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                    {item.status === 0 ? (
                                        <button
                                            onClick={() => handleCompleteClick(item)}
                                            className="px-3 py-1 text-[12px] border border-[#887267] text-[#887267] rounded-lg hover:bg-[#f5f2f0] transition-colors"
                                        >
                                            구비완료
                                        </button>
                                    ) : (
                                        <span className="text-[12px] text-gray-300">-</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* 페이지네이션 */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 mt-5">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                        className="px-3 h-8 border border-[#e8e0dc] rounded-lg text-[12px] text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#f5f2f0]">
                        이전
                    </button>
                    <span className="text-[13px] text-gray-500">{page} / {totalPages}</span>
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                        className="px-3 h-8 border border-[#e8e0dc] rounded-lg text-[12px] text-[#887267] disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#f5f2f0]">
                        다음
                    </button>
                </div>
            )}

            {/* 구비완료 확인 모달 */}
            {confirmModal.open && confirmModal.item && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[360px] mx-4 p-6">
                        <h3 className="text-[16px] font-bold text-[#2a1f1a] mb-2">구비완료 처리하시겠습니까?</h3>
                        <p className="text-[13px] text-gray-400 mb-5">"{confirmModal.item.book_title}" 도서를 구비완료 처리합니다.</p>
                        <div className="flex gap-2">
                            <button onClick={() => setConfirmModal({ open: false, item: null })}
                                className="flex-1 px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 text-[13px]">취소</button>
                            <button onClick={handleCompleteConfirm}
                                className="flex-1 px-4 py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold">확인</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 결과 모달 */}
            {resultModal.open && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[320px] mx-4 p-6 text-center">
                        <p className="text-[14px] font-semibold text-[#2a1f1a] mb-4">{resultModal.title}</p>
                        <button onClick={() => setResultModal({ open: false, title: '' })}
                            className="w-full py-2 bg-[#887267] text-white rounded-lg hover:bg-[#6b5248] text-[13px] font-semibold">확인</button>
                    </div>
                </div>
            )}
        </>
    )
}

export default RequestTab